import React, { useState, useEffect, useRef } from "react"
import { X, Users, Building2, UserCheck, Building, ChevronDown, Check } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { useUsers } from "@/lib/contexts/user-context"

const MassAssignmentModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  workflow, 
  departments = [], 
  users = [],
  csrf_token 
}) => {
  const { currentUser } = useUsers()
  const [assignmentType, setAssignmentType] = useState(workflow?.assignment_type || 'individual')
  const [selectedUserIds, setSelectedUserIds] = useState(workflow?.assigned_user_ids || [])
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState(workflow?.assigned_department_ids || [])
  const [searchQuery, setSearchQuery] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [overrideExistingTasks, setOverrideExistingTasks] = useState(false)
  const modalRef = useRef(null)

  // Reset form when workflow changes
  useEffect(() => {
    if (workflow) {
      setAssignmentType(workflow.assignment_type || 'individual')
      setSelectedUserIds(workflow.assigned_user_ids || [])
      setSelectedDepartmentIds(workflow.assigned_department_ids || [])
    }
  }, [workflow])

  // Reset form when modal closes (without saving)
  useEffect(() => {
    if (!isOpen && workflow) {
      // Reset to original values when modal closes
      setAssignmentType(workflow.assignment_type || 'individual')
      setSelectedUserIds(workflow.assigned_user_ids || [])
      setSelectedDepartmentIds(workflow.assigned_department_ids || [])
      setSearchQuery("")
      setOverrideExistingTasks(false)
    }
  }, [isOpen, workflow])

  // Handle click outside modal to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  const assignmentTypes = [
    {
      value: 'individual',
      label: 'Individual Users',
      description: 'Assign to specific users',
      icon: <Users className="h-4 w-4" />
    },
    {
      value: 'department',
      label: 'Department',
      description: 'Assign to all users in selected departments (includes sub-departments)',
      icon: <Building2 className="h-4 w-4" />
    },
    {
      value: 'department_manager',
      label: 'Department Managers',
      description: 'Assign to managers of selected departments',
      icon: <UserCheck className="h-4 w-4" />
    }
  ]

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Enhanced filtering that includes parents when children match
  const filteredDepartments = (() => {
    if (!searchQuery.trim()) {
      return departments
    }
    
    const query = searchQuery.toLowerCase()
    const matchingDepartments = new Set()
    
    // First, find all departments that match the search query
    departments.forEach(dept => {
      if (dept.name.toLowerCase().includes(query)) {
        matchingDepartments.add(dept.id)
        
        // Also include all parent departments of matching departments
        const addParents = (departmentId) => {
          const dept = departments.find(d => d.id === departmentId)
          if (dept && dept.parent_id) {
            matchingDepartments.add(dept.parent_id)
            addParents(dept.parent_id) // Recursively add all parents
          }
        }
        addParents(dept.id)
      }
    })
    
    // Return all departments that either match or are parents of matches
    return departments.filter(dept => matchingDepartments.has(dept.id))
  })()

  // Organize departments by hierarchy for display only
  const organizeDepartmentsByHierarchy = (depts) => {
    const departmentMap = new Map()
    const rootDepartments = []
    
    // Create a map of all departments
    depts.forEach(dept => {
      departmentMap.set(dept.id, { ...dept, children: [] })
    })
    
    // Build hierarchy
    depts.forEach(dept => {
      if (dept.parent_id) {
        const parent = departmentMap.get(dept.parent_id)
        if (parent) {
          parent.children.push(departmentMap.get(dept.id))
        }
      } else {
        rootDepartments.push(departmentMap.get(dept.id))
      }
    })
    
    // Flatten hierarchy for display with indentation
    const flattenWithIndentation = (dept, level = 0) => {
      const result = [{ ...dept, level, indent: level * 20 }]
      dept.children.forEach(child => {
        result.push(...flattenWithIndentation(child, level + 1))
      })
      return result
    }
    
    return rootDepartments.flatMap(dept => flattenWithIndentation(dept))
  }

  const hierarchicalDepartments = organizeDepartmentsByHierarchy(filteredDepartments)

  const handleUserToggle = (userId) => {
    if (selectedUserIds.includes(userId)) {
      setSelectedUserIds(prev => prev.filter(id => id !== userId))
    } else {
      setSelectedUserIds(prev => [...prev, userId])
    }
  }

  const handleDepartmentToggle = (departmentId) => {
    if (selectedDepartmentIds.includes(departmentId)) {
      // Deselecting - remove this specific department only
      setSelectedDepartmentIds(prev => prev.filter(id => id !== departmentId))
    } else {
      // Selecting - add this specific department only
      setSelectedDepartmentIds(prev => [...prev, departmentId])
    }
  }

  // Check if a department is selected
  const isDepartmentSelected = (departmentId) => {
    return selectedDepartmentIds.includes(departmentId)
  }

  // Check if a department has children
  const hasChildren = (departmentId) => {
    return departments.some(dept => dept.parent_id === departmentId)
  }

  // Get all child department IDs for a parent (recursively - includes all descendants)
  const getChildDepartmentIds = (parentId) => {
    const directChildren = departments.filter(dept => dept.parent_id === parentId)
    let allChildren = []
    
    directChildren.forEach(child => {
      allChildren.push(child.id)
      // Recursively get children of children
      allChildren.push(...getChildDepartmentIds(child.id))
    })
    
    return allChildren
  }

  // Handle parent department selection (with children option)
  const handleParentDepartmentToggle = (departmentId, selectChildren = false) => {
    if (selectChildren) {
      // Select parent and all children
      const childIds = getChildDepartmentIds(departmentId)
      const allIds = [departmentId, ...childIds]
      const newIds = allIds.filter(id => !selectedDepartmentIds.includes(id))
      setSelectedDepartmentIds(prev => [...prev, ...newIds])
    } else {
      // Just select the parent individually
      handleDepartmentToggle(departmentId)
    }
  }

  // Find department managers for selected departments
  const getDepartmentManagers = (departmentIds) => {
    console.log('MassAssignmentModal: getDepartmentManagers called', {
      assignmentType,
      departmentIds,
      usersCount: users.length,
      users: users.map(u => ({ 
        id: u.id, 
        name: u.name, 
        department_id: u.department_id, 
        roles: u.roles 
      }))
    })
    
    if (assignmentType !== 'department_manager' || !departmentIds.length) {
      return []
    }
    
    // Find users in selected departments who have manager role
    const foundUsers = users.filter(user => 
      departmentIds.includes(user.department_id) && 
      user.roles && 
      user.roles.some(role => role.name === 'manager')
    )
        
    return foundUsers.map(user => user.id)
  }

  // Get all users from selected departments
  const getDepartmentUsers = (departmentIds) => {
    
    if (assignmentType !== 'department' || !departmentIds.length) {
      return []
    }
    
    const foundUsers = users.filter(user => 
      departmentIds.includes(user.department_id)
    )
    
    return foundUsers.map(user => user.id)
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {

      // Resolve the actual user IDs based on assignment type
      let resolvedUserIds = selectedUserIds
      if (assignmentType === 'department' && selectedDepartmentIds.length > 0) {
        resolvedUserIds = getDepartmentUsers(selectedDepartmentIds)
      } else if (assignmentType === 'department_manager' && selectedDepartmentIds.length > 0) {
        resolvedUserIds = getDepartmentManagers(selectedDepartmentIds)
      }

      const assignmentData = {
        assignment_type: assignmentType,
        assigned_user_ids: assignmentType === 'individual' ? selectedUserIds : resolvedUserIds,
        assigned_department_ids: ['department', 'department_manager'].includes(assignmentType) ? selectedDepartmentIds : null,
        override_existing_tasks: overrideExistingTasks
      }

      await onSave(workflow.id, assignmentData, csrf_token)
      onClose()
    } catch (error) {
      console.error('Failed to save assignment:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const getSelectedCount = () => {
    switch (assignmentType) {
      case 'individual':
        return selectedUserIds.length
      case 'department':
      case 'department_manager':
        return selectedDepartmentIds.length
      default:
        return 0
    }
  }

  const getSelectedItems = () => {
    switch (assignmentType) {
      case 'individual':
        return users.filter(user => selectedUserIds.includes(user.id))
      case 'department':
      case 'department_manager':
        return departments.filter(dept => selectedDepartmentIds.includes(dept.id))
      default:
        return []
    }
  }

  // Get all users that will be assigned based on current selection
  const getAssignedUsers = () => {
    if (assignmentType === 'individual') {
      const result = users.filter(user => selectedUserIds.includes(user.id))
      return result
    } else if (assignmentType === 'department') {
      // Get all users from selected departments
      const result = users.filter(user => 
        selectedDepartmentIds.includes(user.department_id)
      )
      return result
    } else if (assignmentType === 'department_manager') {
      // Get all managers from selected departments only
      
      const result = users.filter(user => 
        selectedDepartmentIds.includes(user.department_id) && 
        user.roles && 
        user.roles.some(role => role.name === 'manager')
      )
      return result
    }
    return []
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div ref={modalRef} className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl h-full sm:h-auto overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b dark:border-gray-700">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Assign Workflow
              </h2>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={overrideExistingTasks}
                  onChange={(e) => setOverrideExistingTasks(e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Override existing task assignments
                </span>
              </label>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 flex flex-col">
             {overrideExistingTasks && (
                <span className="text-amber-600 dark:text-amber-400 font-medium">
                  • Will update all existing task assignments
                </span>
              )}
              {workflow?.name}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Assignment Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Assignment Type
            </label>
            <div className="grid grid-cols-1 gap-2">
              {assignmentTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setAssignmentType(type.value)}
                  className={`p-3 rounded-lg border text-left transition-colors ${
                    assignmentType === type.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-1 rounded ${
                      assignmentType === type.value
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-gray-400 dark:text-gray-500'
                    }`}>
                      {type.icon}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {type.label}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {type.description}
                      </div>
                    </div>
                    {assignmentType === type.value && (
                      <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div>
            <Input
              type="text"
              placeholder={`Search ${assignmentType === 'individual' ? 'users' : 'departments'}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full"
            />
          </div>

          {/* Selection List */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {assignmentType === 'individual' ? 'Select Users' : 'Select Departments'}
              </label>
              <Badge variant="secondary">
                {getSelectedCount()} selected
              </Badge>
            </div>

            <div className="max-h-64 overflow-y-auto border rounded-lg dark:border-gray-600">
              {assignmentType === 'individual' ? (
                <div className="divide-y dark:divide-gray-600">
                  {filteredUsers.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => handleUserToggle(user.id)}
                      className={`w-full p-3 text-left  flex items-center gap-3 ${
                        selectedUserIds.includes(user.id)
                          ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/50'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                        selectedUserIds.includes(user.id)
                          ? 'border-blue-500 bg-blue-500'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {selectedUserIds.includes(user.id) && (
                          <Check className="h-3 w-3 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 dark:text-white truncate">
                          {user.name}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                          {user.email}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="divide-y dark:divide-gray-600">
                  {hierarchicalDepartments.map((department) => {
                    const isSelected = isDepartmentSelected(department.id)
                    const isParent = hasChildren(department.id)
                    const childIds = getChildDepartmentIds(department.id)
                    const allChildrenSelected = childIds.length > 0 && childIds.every(id => selectedDepartmentIds.includes(id))
                    
                    return (
                      <div key={department.id} className="relative">
                        <button
                          onClick={() => handleDepartmentToggle(department.id)}
                          className={`w-full p-3 text-left flex items-center gap-3 ${
                            isSelected ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/50' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                          style={{ paddingLeft: `${12 + department.indent}px` }}
                        >
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            isSelected
                              ? 'border-blue-500 bg-blue-500'
                              : 'border-gray-300 dark:border-gray-600'
                          }`}>
                            {isSelected && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 dark:text-white truncate flex items-center gap-2">
                              {department.level > 0 && (
                                <span className="text-gray-400 dark:text-gray-500 text-sm">
                                  {'└─ '}
                                </span>
                              )}
                              {department.name}
                            </div>
                            {department.description && (
                              <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                                {department.description}
                              </div>
                            )}
                          </div>
                        </button>
                        
                        {/* Parent department "Select All Children" button */}
                        {isParent && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              if (allChildrenSelected) {
                                // Deselect all children and the parent itself
                                const childIds = getChildDepartmentIds(department.id)
                                const allIds = [department.id, ...childIds]
                                setSelectedDepartmentIds(prev => 
                                  prev.filter(id => !allIds.includes(id))
                                )
                              } else {
                                // Select all children
                                handleParentDepartmentToggle(department.id, true)
                              }
                            }}
                            className={`absolute right-2 top-1/2 transform -translate-y-1/2 px-2 py-1 text-xs rounded transition-colors ${
                              allChildrenSelected
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
                            }`}
                            title={allChildrenSelected ? "Deselect all child departments" : "Select all child departments"}
                          >
                            {allChildrenSelected ? 'Deselect All' : 'Select All'}
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Selected Items Summary */}
          {getSelectedCount() > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Selected {assignmentType === 'individual' ? 'Users' : 'Departments'}
              </label>
              <div className="flex flex-wrap gap-2">
                {getSelectedItems().map((item) => (
                  <Badge key={item.id} variant="outline" className="text-xs">
                    {item.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Assigned Users Preview */}
          {assignmentType !== 'individual' && getSelectedCount() > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Users to be Assigned ({getAssignedUsers().length})
              </label>
              <div className="max-h-32 overflow-y-auto border rounded-lg dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50">
                <div className="p-3">
                  {getAssignedUsers().length > 0 ? (
                    <div className="space-y-1">
                      {getAssignedUsers().slice(0, 10).map((user) => (
                        <div key={user.id} className="flex items-center gap-2 text-sm">
                          <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-xs font-medium text-blue-700 dark:text-blue-300">
                            {user.firstName?.[0]}{user.lastName?.[0]}
                          </div>
                          <span className="text-gray-900 dark:text-white">
                            {user.firstName} {user.lastName}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400 text-xs">
                            ({user.email})
                          </span>
                        </div>
                      ))}
                      {getAssignedUsers().length > 10 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 pt-1">
                          ... and {getAssignedUsers().length - 10} more users
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                      No users found in selected departments
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={isSaving || getSelectedCount() === 0}
            className="bg-blue-600 hover:bg-blue-700 dark:text-white"
          >
            {isSaving ? 'Saving...' : 'Save Assignment'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default MassAssignmentModal
